import React, { useEffect, useMemo, useState } from 'react';
import { useSurvey } from './index';
import { formatCurrency } from '../common/utils';
import { TrophyIcon } from '../icons';
import { SURVEY_QUESTION_TYPE } from '../constants';
import { readStoredStep, writeStoredStep, clearStoredStep } from './session';
import SurveyCompleteAnimation from './SurveyCompleteAnimation';


const SurveyViewer = () => {
    const { activeSurvey, setActiveSurvey, answers, setAnswer, submitSurvey } = useSurvey();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [stepIndex, setStepIndex] = useState(() => activeSurvey ? readStoredStep(activeSurvey.id) : 0);
    const [error, setError] = useState<string | null>(null);

    const questions = activeSurvey?.questions ?? [];
    const totalSteps = questions.length;
    const currentQuestion = questions[stepIndex];
    const isLastStep = stepIndex === totalSteps - 1;
    const progressPercent = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;

    useEffect(() => {
        if (activeSurvey) {
            writeStoredStep(activeSurvey.id, stepIndex);
        }
    }, [stepIndex, activeSurvey]);

    const currentAnswerValue = useMemo<string | string[]>(() => {
        if (!activeSurvey || !currentQuestion) return '';
        const surveyAnswers = answers[activeSurvey.id] ?? [];
        return surveyAnswers.find(a => a.questionId === currentQuestion.id)?.answer ?? '';
    }, [answers, activeSurvey, currentQuestion]);

    const currentTextAnswer = typeof currentAnswerValue === 'string' ? currentAnswerValue : '';
    const currentSelectedOptions = Array.isArray(currentAnswerValue) ? currentAnswerValue : [];

    const handleAnswer = (answer: string) => {
        if (!activeSurvey || !currentQuestion) return;
        setAnswer(activeSurvey.id, { questionId: currentQuestion.id, answer });
        if (error) setError(null);
    };

    const handleToggleOption = (optionText: string) => {
        if (!activeSurvey || !currentQuestion) return;
        const updated = currentSelectedOptions.includes(optionText)
            ? currentSelectedOptions.filter(o => o !== optionText)
            : [...currentSelectedOptions, optionText];
        setAnswer(activeSurvey.id, { questionId: currentQuestion.id, answer: updated });
        if (error) setError(null);
    };

    const handleSubmit = async () => {
        if (!activeSurvey) return;
        setSubmitting(true);
        const success = await submitSurvey(activeSurvey.id);
        setSubmitting(false);

        if (!success) {
            setError('Something went wrong submitting your answers. Please try again.');
            return;
        }

        clearStoredStep(activeSurvey.id);

        setSubmitted(true);
        setTimeout(() => setActiveSurvey(null), 3500);
    };

    const handleNext = () => {
        if (!currentQuestion) return;

        const answered = Array.isArray(currentAnswerValue)
            ? currentAnswerValue.length > 0
            : !!currentAnswerValue;

        if (!answered) {
            setError('Please answer this question before continuing.');
            return;
        }

        setError(null);

        if (isLastStep) {
            handleSubmit();
        } else {
            setStepIndex(i => i + 1);
        }
    };

    const handleExit = () => {
        setError(null);
        setActiveSurvey(null);
    };

    const handlePrevious = () => {
        if (stepIndex === 0) return;
        setError(null);
        setStepIndex(i => i - 1);
    };

    return submitted ? (
        <SurveyCompleteAnimation />
    ) : !activeSurvey || !currentQuestion ? null : (
        <div className="survey-view">
            <div className="survey-view-header">
                <button className="survey-back-btn" onClick={handleExit}>
                    ← Back
                </button>
                <span className="survey-step-count">{stepIndex + 1} of {totalSteps}</span>
            </div>

            <div className="survey-progress-track">
                <div className="survey-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>

            <div className="survey-step-tag">{activeSurvey.title}</div>
            <p className="survey-question-text">{currentQuestion.text}</p>

            {currentQuestion.type === SURVEY_QUESTION_TYPE.MULTIPLE_CHOICE && (
                <div className="survey-options">
                    {currentQuestion.options.map(option => (
                        <button
                            key={option.text}
                            className={`survey-option ${currentTextAnswer === option.text ? 'survey-option--selected' : ''}`}
                            onClick={() => handleAnswer(option.text)}
                        >
                            {option.text}
                        </button>
                    ))}
                </div>
            )}

            {currentQuestion.type === SURVEY_QUESTION_TYPE.SELECT_ALL && (
                <div className="survey-options">
                    {currentQuestion.options.map(option => {
                        const isSelected = currentSelectedOptions.includes(option.text);
                        return (
                            <button
                                key={option.text}
                                className={`survey-option survey-option--checkbox ${isSelected ? 'survey-option--selected' : ''}`}
                                onClick={() => handleToggleOption(option.text)}
                            >
                                <span className="survey-option-checkbox">{isSelected && '✓'}</span>
                                {option.text}
                            </button>
                        );
                    })}
                </div>
            )}

            {currentQuestion.type === SURVEY_QUESTION_TYPE.TEXT && (
                <textarea
                    className="survey-textarea"
                    value={currentTextAnswer}
                    onChange={e => handleAnswer(e.target.value)}
                    placeholder="Your answer..."
                    maxLength={500}
                    rows={4}
                />
            )}

            {currentQuestion.type === SURVEY_QUESTION_TYPE.RATING && (
                <div className="survey-rating">
                    {[1, 2, 3, 4, 5].map(rating => (
                        <button
                            key={rating}
                            className={`survey-rating-btn ${currentTextAnswer === String(rating) ? 'survey-rating-btn--selected' : ''}`}
                            onClick={() => handleAnswer(String(rating))}
                        >
                            {rating}
                        </button>
                    ))}
                </div>
            )}

            { error && <p className="survey-error">{ error }</p> }

            { isLastStep && (
                <div className="survey-submit-reward">
                    <TrophyIcon />
                    <span>You'll earn <strong>{ formatCurrency(activeSurvey.rewardAmount) }</strong> when you submit</span>
                </div>
            )}

            <div className="survey-step-footer">
                { stepIndex > 0 && (
                    <button className="survey-prev-btn" onClick={handlePrevious}>
                        ← Previous
                    </button>
                )}
                <button
                    className="survey-submit-btn"
                    onClick={handleNext}
                    disabled={submitting}
                >
                    { submitting ? 'Submitting...' : isLastStep ? 'Submit' : 'Next →' }
                </button>
            </div>
        </div>
    );
};

export default SurveyViewer;